import React, { useEffect, useReducer, useState } from "react";
import {
  Pencil,
  Trash,
  Eye,
  ArrowUp,
  ArrowDown,
  RotateCcw,
} from "lucide-react";
import "../styles/table-search.css";
import "../styles/modal.css";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/icon-style.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";
import { useLocation } from "react-router-dom";
import { setPrevEntity } from "../redux/slices/entitySlice";
import "../styles/common/table-heading-icons.css";
import RowDetailsModal from "./RowDetailsModal";
import { setFocusedField, clearFocusedField } from "../redux/slices/focusSlice";
import { useRef } from "react";
import {
  Column,
  SortConfig,
  TableComponentProps,
} from "../models/TableComponent/table";

import '../styles/table.css'

let cachedCurrencyData: any[] = [];

const TableComponent: React.FC<TableComponentProps> = ({
  columns,
  data,
  onEdit,
  onDelete,
  setFilter,
  setSorting,
  onRowClick,
  defaultSortConfig,
  currencyData,
  context = "default",
  excludeFields = [],
}) => {
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);

  const focusedField = useSelector((state: RootState) => state.focus.focusedField);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    if (focusedField && inputRefs.current[focusedField]) {
      inputRefs.current[focusedField]?.focus();
    }
  }, [focusedField]);


  const getInputId = (col: Column): string => {
    return col.nameKey ? `input-${col.key}-${col.nameKey}` : `input-${col.key}`;
  };

  const location = useLocation();
  const prevEntity = useSelector((state: RootState) => state.entity.prevEntity);

  const getMenuTitle = () => {
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    const formatTitle = (text: string) => {
      if (!text) return '';
      const specialCases: Record<string, string> = {
        'itemmodels': 'Item Models',
        'itemcategorys': 'Item Categories',
        'newItem': 'Items',
        'items': 'Items',
        'racks': 'Racks',
        'newRack': 'Racks',
        'uoms': 'UOM Master',
        'currencymaster': 'Currency Master',
        'gstslabs': 'GST Slab Master',
        'inwardoptions': 'Inward Options',
        'outwardoptions': 'Outward Options',
        'itemassignedracks': 'Item Assigned Racks',
        'bomtypes': 'BOM Types',
        'contributors': 'Contributors',
        'customerparts': 'Customer Parts',
        'supplierparts': 'Supplier Parts',
        'citymasters': 'City Master',
        'statemaster': 'State Master',
        'country-masters': 'Country Master',
        'roles': 'Roles',
        'users': 'Users',
        'grns': 'GRN',
        'stocksummary': 'Stock Summary',
        'stocks': 'Current Stock',
        'barcode': 'Barcode',
        'purchase-orders': 'Purchase Orders',
        'pendingpo':'Pending Po Details',
        'pricehistory': 'Price History',
        'RMinventory':"rm-inventory-cost",
        'bom':'bom'
      };
      
      if (specialCases[text]) {
        return specialCases[text];
      }
      return text
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };
    
    return formatTitle(lastPart);
  };

  const [debouncedSearch, setDebouncedSearch] = useState<{
    [key: string]: string;
  }>({});
  const [searchQueries, setSearchQueries] = useState<{ [key: string]: string }>(
    {}
  );
  const [hoveredAction, setHoveredAction] = useState<{
    id: string;
    action: "edit" | "delete" | "view" | null;
  }>({
    id: "",
    action: null,
  });
  const [hoveredTooltip, setHoveredTooltip] = useState<{
    id: string;
    position: "above" | "below";
  }>({
    id: "",
    position: "below",
  });
  const [viewRowData, setViewRowData] = useState<any | null>(null);


  const [currencyOptions, setCurrencyOptions] = useState<any[]>(
    currencyData || cachedCurrencyData || []
  );

  const [sortConfig, setSortConfig] = useState<SortConfig>(
    defaultSortConfig || { key: "", direction: "asc" }
  );


  useEffect(() => {
    if (currencyData && currencyData.length > 0) {
      setCurrencyOptions(currencyData);
      cachedCurrencyData = currencyData;
    }
  }, [currencyData]);

  const enhanceRowData = (row: any) => {
    const enhancedRow = { ...row };
    if (currencyOptions.length > 0) {
      if (enhancedRow.purchaseCurrencyId) {
        const currency = currencyOptions.find(
          (c) => c._id === enhancedRow.purchaseCurrencyId
        );
        if (currency) {
          enhancedRow.purchaseCurrency = currency;
          enhancedRow.purchaseCurrencyName = currency.name;
        }
      }

      if (enhancedRow.sellingCurrencyId) {
        const currency = currencyOptions.find(
          (c) => c._id === enhancedRow.sellingCurrencyId
        );
        if (currency) {
          enhancedRow.sellingCurrency = currency;
          enhancedRow.sellingCurrencyName = currency.name;
        }
      }
    }

    return enhancedRow;
  };

  useEffect(() => {
    if (defaultSortConfig) {
      setSortConfig(defaultSortConfig);
    }
  }, [defaultSortConfig]);

  const handleSearchChange = (key: string, value: string, nameKey?: string) => {
    let updatedKey = key;

    if (nameKey) {
      updatedKey = `${key}.${nameKey}`;
    }

    setSearchQueries((prev) => ({ ...prev, [updatedKey]: value }));
  };


  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearch(searchQueries);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQueries]);

  useEffect(() => {
    Object.keys(debouncedSearch).forEach((key) => {
      dispatch(updateFilters({ key, value: debouncedSearch[key] }));
      setFilter?.(debouncedSearch);
    });
  }, [debouncedSearch, dispatch, setFilter]);

  useEffect(() => {
    setSearchQueries((prevQueries) => ({ ...prevQueries, ...filters }));
  }, []);

  useEffect(() => {
    setSearchQueries((prevQueries) => ({ ...prevQueries, ...filters }));
  }, []);


  const getEntityFromPath = (path: string) => {
    const parts = path.split("/");
    return parts.length > 2 ? parts[parts.length - 1] : "";
  };

  useEffect(() => {
    const currentEntity = getEntityFromPath(location.pathname);

    if (currentEntity !== prevEntity) {
      dispatch(resetFilters());
      setSearchQueries({});
      setDebouncedSearch({});
      dispatch(setPrevEntity(currentEntity));
    }
  }, [location.pathname, prevEntity, dispatch]);


  useEffect(() => {
    if (sortConfig.key && setSorting) {
      setSorting(sortConfig.key, sortConfig.direction);
    }
  }, [sortConfig, setSorting]);

  const handleSort = (key: string) => {
    let newDirection: "asc" | "desc" = "asc";
    if (sortConfig.key === key) {
      newDirection = sortConfig.direction === "asc" ? "desc" : "asc";
    }

    setSortConfig({ key, direction: newDirection });
  };

  const formatCellValue = (value: any, key?: string) => {
    if (value === null || value === undefined) {
      // Return "Unknown State" for stateName property if it's missing
      if (key === "stateName") return "Unknown State";
      return "N/A";
    }
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value);
    }
    return value?.toString() || (key === "stateName" ? "Unknown State" : "N/A");
  };


  const isInvoiceColumn = (key: string, label: string): boolean => {
    return (
      key.toLowerCase().includes("invoice") ||
      label.toLowerCase().includes("invoice")
    );
  };

  const isNumeric = (value: any, column?: Column, key?: string): boolean => {
    if (column?.isNumeric) return true;
    if (key?.toLowerCase() === "hsncode") return false;
    if (column && isInvoiceColumn(column.key, column.label)) return false;
    if (value === null || value === undefined || value === "") return false;
    const stringValue = String(value).trim();

    if (stringValue.toLowerCase() === "n/a") return false;

    const numericValue = Number(stringValue);
    return !isNaN(numericValue) && typeof numericValue === "number";
  };

  const checkTooltipPosition = (
    id: string,
    event: React.MouseEvent<HTMLSpanElement>,
    index: number
  ) => {
    const cellRect = event.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const isLastThreeRows = index >= data.length - 3;

    if (isLastThreeRows || viewportHeight - cellRect.bottom < 50) {
      setHoveredTooltip({ id, position: "above" });
    } else {
      setHoveredTooltip({ id, position: "below" });
    }
  };

  const isInactiveStatus = (row: any): boolean => {
    if (row.status && row.status.toLowerCase() === "inactive") {
      return true;
    }

    if (row.state && row.state.toLowerCase() === "inactive") {
      return true;
    }

    if (
      row.statusInfo &&
      row.statusInfo.status &&
      row.statusInfo.status.toLowerCase() === "inactive"
    ) {
      return true;
    }

    return false;
  };

  const handleViewClick = (row: any) => {
    const enhancedRow = enhanceRowData(row);
    setViewRowData(enhancedRow);
  };


  const permissions = useSelector((state: RootState) => state.permissions)

  const permissionMappings: Record<string, string> = {
    'newItem': 'items',
    'itemCategory': 'itemcategorys',
    'itemModel': 'itemmodels',
    'Inward': 'inwardoptions',
    'newRack': 'racks',
    'Outward': 'outwardoptions',
    'itemAssignedRack': 'itemassignedracks',
    'bomtypes': 'bomtypes',
    'newContributor': 'contributors',
    'customerParts': 'customerparts',
    'supplierParts': 'supplierparts',
    'cityMaster': 'citymasters',
    'stateMaster': 'statemaster',
    'countryMaster': 'country-masters',
    'currencyMaster': 'currencymaster',
    'gstSlabMaster': 'gstslabs',
    'uomMaster': 'uoms',
    'approvalRole': 'roles',
    'newGRN': 'grns',
    'grnSummary': 'grns',
    'grnReport': 'grns',
    'incobar': 'barcode',
    'CurrentStock': 'stocks',
    'stocksummary': 'stocksummary',
    'users': 'users',
    'roles': 'roles',
    'purchaseorder': 'purchase-orders',
     'pricehistory':'pricehistory'
  };

  const currentEntity = getEntityFromPath(location.pathname);
  const permissionKey = permissionMappings[currentEntity] || currentEntity;
  const entityPermissions = permissions.menus[permissionKey];

  return (
    <div className="item-models-table-wrapper">
      <table className="item-models-table">
        <thead className="table-header">
          <tr>
            {columns.map((col) => (
              <th key={col.key}>
                <div className="table-header-container">
                  <div className="table-heading-with-sort">
                    <span className="table-heading">{col.label}</span>

                    {col.sortable !== false && (
                      <div className="sort-icons">
                        <button
                          className={`sort-icon ${sortConfig.key === col.key &&
                              sortConfig.direction === "asc"
                              ? "active"
                              : ""
                            }`}
                          onClick={() => handleSort(col.key)}
                          aria-label={`Sort ${col.label} ascending`}
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          className={`sort-icon ${sortConfig.key === col.key &&
                              sortConfig.direction === "desc"
                              ? "active"
                              : ""
                            }`}
                          onClick={() => handleSort(col.key)}
                          aria-label={`Sort ${col.label} descending`}
                        >
                          <ArrowDown size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="table-cell-search-container">
                    {col.key === "status" ? (
                      <select
                        className="search-dropdown"
                        value={
                          searchQueries[
                          col.nameKey ? `${col.key}.${col.nameKey}` : col.key
                          ] || ""
                        }
                        onChange={(e) =>
                          handleSearchChange(
                            col.key,
                            e.target.value,
                            col.nameKey
                          )
                        }
                      >
                        <option value="">ALL</option>
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="search-input"
                        placeholder={col.label}
                        value={
                          searchQueries[
                          col.nameKey ? `${col.key}.${col.nameKey}` : col.key
                          ] || ""
                        }
                        onChange={(e) =>
                          handleSearchChange(
                            col.key,
                            e.target.value,
                            col.nameKey
                          )
                        }
                        onFocus={() =>
                          dispatch(setFocusedField(getInputId(col)))
                        }
                        onBlur={() => dispatch(clearFocusedField())}
                        ref={(el) => {
                          inputRefs.current[getInputId(col)] = el;
                        }}
                      />
                    )}
                  </div>
                </div>
              </th>
            ))}
            <th>
              Actions
              <RotateCcw
                size={16}
                className="reset-search"
                onClick={() => {
                  dispatch(resetFilters());
                  setSearchQueries({});
                  setDebouncedSearch({});
                }}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} style={{ textAlign: "center" }}>
                No records found.
              </td>
            </tr>
          ) : (
            data.map((row, index) => {
              const rowHasInactiveStatus = isInactiveStatus(row);

              return (
                <tr key={index} onClick={() => onRowClick && onRowClick(row)} className="clickable-row">
                  {columns.map((col) => {
                    const cellValue = row[col.key] !== null && row[col.key] !== undefined
                      ? formatCellValue(row[col.key], col.key)
                      : (col.key === "stateName" ? "Unknown State" : "N/A");
                    const isLongText = cellValue.length > 35;
                    const isInvoice = isInvoiceColumn(col.key, col.label);
                    const isNumericCell = isNumeric(row[col.key], col, col.key);

                    if (col.key === "stockStatus") {
                      return (
                        <td
                          key={col.key}
                          className={`cell-content ${isNumericCell ? "numeric-cell" : ""
                            }`}
                          style={{
                            textAlign: col.isRightAlign ? "right" : "left",
                          }}
                        >
                          <div className="status-display">
                            <span>{cellValue}</span>
                            {cellValue && cellValue !== "N/A" && (
                              <img
                                src={`/images/${cellValue.toLowerCase()}Icon.png`}
                                alt={``}
                                className="status-icon"
                                style={{ marginLeft: '8px', width: '20px', height: '20px' }}
                              />
                            )}
                          </div>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={col.key}
                        className={`cell-content ${isNumericCell ? "numeric-cell" : ""
                          }`}
                        style={{
                          textAlign: col.isRightAlign ? "right" : "left",
                        }}
                      >
                        {col.render ? (
                          col.render(row[col.key], row)
                        ) : (
                          <>
                            <span
                              className={isLongText ? "truncated-text" : ""}
                              onMouseEnter={(e) =>
                                checkTooltipPosition(
                                  row._id || index.toString(),
                                  e,
                                  index
                                )
                              }
                            >
                              {isLongText
                                ? `${cellValue.substring(0, 35)}...`
                                : cellValue}
                            </span>
                            {isLongText && (
                              <div
                                className="tooltip-text"
                                data-position={
                                  hoveredTooltip.id ===
                                    (row._id || index.toString())
                                    ? hoveredTooltip.position
                                    : "below"
                                }
                              >
                                {cellValue}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
                  <td  key="actions" onClick={(e) => e.stopPropagation()}
                   className="item-models-actions">
                    <div className="action-container">
                      <div
                        className="icon-wrapper view-icon-wrapper"
                        onMouseEnter={() =>
                          setHoveredAction({
                            id: row._id || index.toString(),
                            action: "view",
                          })
                        }
                        onMouseLeave={() =>
                          setHoveredAction({ id: "", action: null })
                        }
                      >
                        <button
                          className="view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewClick(row);
                          }}
                        >
                          <Eye size={16} />
                        </button>
                        {hoveredAction.id === (row._id || index.toString()) &&
                          hoveredAction.action === "view" && (
                            <span className="action-tooltip view-tooltip">
                              View
                            </span>
                          )}
                      </div>

                      {onEdit && entityPermissions?.update && (
                        <div
                          className="icon-wrapper edit-icon-wrapper"
                          onMouseEnter={() =>
                            setHoveredAction({
                              id: row._id || index.toString(),
                              action: "edit",
                            })
                          }
                          onMouseLeave={() =>
                            setHoveredAction({ id: "", action: null })
                          }
                        >
                          <button
                            className="edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(row)
                            }}
                          >
                            <Pencil size={16} />
                          </button>
                          {hoveredAction.id === (row._id || index.toString()) &&
                            hoveredAction.action === "edit" && (
                              <span className="action-tooltip edit-tooltip">
                                Edit
                              </span>
                            )}
                        </div>
                      )}

                      {onDelete && entityPermissions?.delete && (
                        <div
                          className={`icon-wrapper delete-icon-wrapper ${rowHasInactiveStatus ? "disabled" : ""
                            }`}
                          onMouseEnter={() =>
                            setHoveredAction({
                              id: row._id || index.toString(),
                              action: "delete",
                            })
                          }
                          onMouseLeave={() =>
                            setHoveredAction({ id: "", action: null })
                          }
                        >
                          <button
                            className={`delete-btn ${rowHasInactiveStatus ? "disabled" : ""
                              }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                !rowHasInactiveStatus && onDelete(row._id)
                              }
                            }
                            disabled={rowHasInactiveStatus}
                            style={{
                              opacity: rowHasInactiveStatus ? 0.5 : 1,
                              cursor: rowHasInactiveStatus
                                ? "not-allowed"
                                : "pointer",
                            }}
                          >
                            <Trash size={16} />
                          </button>
                          {hoveredAction.id === (row._id || index.toString()) &&
                            hoveredAction.action === "delete" && (
                              <span className="action-tooltip delete-tooltip">
                                {rowHasInactiveStatus ? "InActive" : "InActive"}
                              </span>
                            )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {viewRowData && (
        <RowDetailsModal
          rowData={viewRowData}
          onClose={() => setViewRowData(null)}
          context={context as "default" | "supplierParts"}
          excludeFields={excludeFields}
          menuTitle={getMenuTitle()} 
        />
      )}
    </div>
  );
};

export default TableComponent;
